import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Target, X } from 'lucide-react'
import { useSetting } from '../../hooks'
import { useGoalCreation } from './creation/shared/GoalCreationContext'
import { NO_GOAL_COPY, noGoalReason } from './noGoalNudgeRules'

/**
 * Daily Overview nudge for an account with nothing to aim at: a dashed, low-chrome card that reads
 * as a suggestion, above the day's lists. Dismissal is a user setting rather than local state
 * because someone who runs GT without goals has to be able to put this away for good, on every
 * device — the same contract TrialUpgradeBanner uses.
 *
 * Whether it shows at all lives in noGoalReason(); this component only draws it.
 */
const NoGoalNudge = () => {
    const { allGoals, areGoalsLoading, areGoalsUnavailable, openFlow } = useGoalCreation()
    const navigate = useNavigate()
    const { field_value: dismissedValue, updateSetting, isLoading } = useSetting('has_dismissed_goal_nudge')

    const reason = noGoalReason({
        goals: allGoals,
        // A setting still in flight is treated as "not loaded yet" for the same reason goals are:
        // showing the card and then yanking it away once the dismissal arrives is worse than waiting.
        areGoalsLoading: areGoalsLoading || isLoading,
        areGoalsUnavailable,
        isDismissed: dismissedValue === 'true',
    })
    if (!reason) return null

    const { title, body, action } = NO_GOAL_COPY[reason]

    return (
        <div
            style={{ animationFillMode: 'both' }}
            className="mb-2 rounded-gt-md border border-border bg-background px-3 py-2.5 animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
            data-testid="no-goal-nudge"
        >
            {/* One row: the actions sit beside the two lines rather than under them, so the card
                costs the day's list about as much height as a single task. It wraps below ~360px. */}
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
                <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-gt-violet/10">
                    <Target className="size-3.5 text-gt-violet" />
                </div>
                <div className="min-w-[11rem] flex-1">
                    <h3 className="text-title-sm text-foreground">{title}</h3>
                    <p className="text-pretty text-body-sm text-muted-foreground">{body}</p>
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-1">
                    <Button
                        size="sm"
                        onClick={() => (reason === 'none' ? openFlow() : navigate('/goals'))}
                        className="bg-action text-action-foreground transition-transform hover:bg-action-hover active:scale-[0.96]"
                    >
                        {action}
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Dismiss"
                        onClick={() => updateSetting('true')}
                        className="size-7 p-0 text-muted-foreground transition-transform active:scale-[0.96]"
                    >
                        <X className="size-4" />
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default NoGoalNudge
